import type { AgentToolAdapter } from './adapter';
import type { AgentTool, AgentToolScope } from './agent-tool';
import { requireConfirmation, type Confirm } from './confirmation';

export interface AgentToolRegistry {
  replaceScope(scope: AgentToolScope, key: string, tools: readonly AgentTool[]): void;
  clearScope(scope: AgentToolScope, key: string): void;
  names(): readonly string[];
  /** As ferramentas registradas agora no escopo (o `get_screen_guide` lista as da tela aberta). */
  toolsIn(scope: AgentToolScope, key: string): readonly AgentTool[];
}

export function createAgentToolRegistry(options: {
  adapter: AgentToolAdapter;
  confirm: Confirm | null;
  declinedMessage: string;
  onRefused?: (tool: AgentTool, reason: unknown) => void;
}): AgentToolRegistry {
  const byScope = new Map<string, AgentTool[]>();
  const id = (scope: AgentToolScope, key: string) => `${scope}:${key}`;

  function clearScope(scope: AgentToolScope, key: string) {
    for (const tool of byScope.get(id(scope, key)) ?? []) options.adapter.unregisterTool(tool.name);
    byScope.delete(id(scope, key));
  }

  return {
    replaceScope(scope, key, tools) {
      clearScope(scope, key);
      const registered: AgentTool[] = [];
      for (const tool of tools) {
        const guarded = requireConfirmation(tool, options.confirm, options.declinedMessage);
        if (!guarded) {
          options.onRefused?.(tool, 'write-without-confirmation');
          continue;
        }
        try {
          options.adapter.registerTool(guarded);
          registered.push(guarded);
        } catch (error) {
          options.onRefused?.(tool, error);
        }
      }
      byScope.set(id(scope, key), registered);
    },
    clearScope,
    names: () => options.adapter.registeredNames(),
    toolsIn: (scope, key) => byScope.get(id(scope, key)) ?? [],
  };
}
